import { useDisclosure } from '@nextui-org/react'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import BaseModal from '~/components/modals/base/BaseModal'
import store, { RootState } from '~/store'
import { setModalParams } from '~/store/modalSlice'
import { AppEvents, globalEventEmitter } from '~/utils/shared'
import { cm } from '~/utils/utils'

export enum ModalId {
  TaskStatusDetails = 'task-status-details-modal',
  FreeTrialModal = 'free-trial-modal',
  SignupModal = 'signup-modal'
}

interface CommonModalProps<T> {
  id: ModalId
  Component: React.FC<T>
  className?: string
  contentClassName?: string
}

export function CommonModal<T extends {}>({
  Component,
  id,
  className,
  contentClassName
}: CommonModalProps<T>) {
  const { isOpen, onOpen, onClose, onOpenChange } = useDisclosure()
  const props = useSelector((state: RootState) => state.modals.modalParams[id])

  useEffect(() => {
    const handleOpen = ({ id: modalId, params }: { id: ModalId; params: T }) => {
      if (modalId === id) {
        store.dispatch(setModalParams({ id, params }))
        onOpen()
      }
    }
    const handleClose = ({ id: modalId }: { id: ModalId }) => {
      if (modalId === id) {
        onClose()
      }
    }

    globalEventEmitter.on(AppEvents.OpenModal, handleOpen)
    globalEventEmitter.on(AppEvents.CloseModal, handleClose)

    return () => {
      globalEventEmitter.off(AppEvents.OpenModal, handleOpen)
      globalEventEmitter.off(AppEvents.CloseModal, handleClose)
    }
  }, [])

  return (
    <BaseModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={props?.title ?? null}
      className={cm('absolute shadow-heavy min-w-[400px] max-h-[90vh] max-w-[85vw]', 'w-fit h-fit')}
      headerBorder={false}
      bodyClassName='p-0'
      wrapperClassName={className}
      contentClassName={contentClassName}
    >
      <Component {...(props as T)} />
    </BaseModal>
  )
}

export function useModalController<T>(id: ModalId) {
  return {
    openModal: (params: T) => {
      globalEventEmitter.emit(AppEvents.OpenModal, { id, params })
    },
    closeModal: () => {
      globalEventEmitter.emit(AppEvents.CloseModal, { id })
    }
  }
}
